import type { OnInit, Signal } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  linkedSignal,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import type { OnyxConfirmModalData } from '@onyx/angular';
import {
  DropdownHelper,
  OnyxConfirmModalComponent,
  OnyxDropdownComponent,
  OnyxFormMode,
  OnyxModalService,
  OnyxSectionComponent,
  OnyxTableComponent,
  OnyxToggleComponent,
  UtilityHelper,
} from '@onyx/angular';
import { differenceBy } from 'lodash';
import { map, tap } from 'rxjs';
import { SHORT_PAGINATION } from '../../../../../common/constants/common/short-pagination';
import { CommonHelper } from '../../../../../common/helpers/common.helper';
import { DriverHelper } from '../../../../drivers/common/helpers/driver.helper';
import type {
  Driver,
  SimplifiedDriver,
} from '../../../../drivers/common/interfaces/driver';
import { BASE_ASSIGNED_DRIVERS_FORM_COLUMNS } from '../../common/constants/base-assigned-drivers-form-columns';
import { BASE_ASSIGNED_DRIVERS_NOT_FOUND } from '../../common/constants/base-assigned-drivers-not-found';

type BaseAssignedDriversFormArray = ReturnType<
  (typeof BaseAssignedDriversFormComponent)['buildForm']
>;
export type BaseAssignedDriversFormGroup = ReturnType<
  (typeof BaseAssignedDriversFormComponent)['buildAssignedDriverForm']
>;

@Component({
  selector: 'app-base-assigned-drivers-form',
  imports: [
    OnyxSectionComponent,
    ReactiveFormsModule,
    OnyxDropdownComponent,
    OnyxTableComponent,
    OnyxToggleComponent,
  ],
  templateUrl: './base-assigned-drivers-form.component.html',
  styleUrl: './base-assigned-drivers-form.component.scss',
})
export class BaseAssignedDriversFormComponent implements OnInit {
  private readonly modalService = inject(OnyxModalService);
  private readonly injector = inject(Injector);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly driverHelper = inject(DriverHelper);

  public readonly form = input.required<BaseAssignedDriversFormArray>();
  public readonly mode = input(OnyxFormMode.ADD);

  protected readonly I18N = 'bases.assignedDrivers';
  protected readonly NOT_FOUND = BASE_ASSIGNED_DRIVERS_NOT_FOUND;
  protected readonly COLUMNS = BASE_ASSIGNED_DRIVERS_FORM_COLUMNS;
  protected readonly DRIVERS_SOURCE = this.driverHelper.getDriversSource();

  protected drivers = signal<Driver[] | null>(null);
  protected pagination = signal(SHORT_PAGINATION);
  protected totalDriversCount = signal<number | null>(null);

  protected assignAllDrivers = linkedSignal(
    () => this.drivers()?.length === this.totalDriversCount(),
  );

  protected driversList = computed(() =>
    CommonHelper.getPaginatedData(this.driversControls(), this.pagination()),
  );

  protected get driversControl() {
    return this.form().controls.drivers;
  }

  private driversControls!: Signal<typeof this.driversControl.controls>;

  public ngOnInit(): void {
    this.driversControls = toSignal(
      this.driversControl.valueChanges.pipe(
        map(() => this.driversControl.controls),
      ),
      { initialValue: this.driversControl.controls, injector: this.injector },
    );

    this.form().controls.selectedDrivers.setValue(
      this.driversControl.getRawValue().map((group) => group.uuid),
    );

    effect(
      () => {
        const selectedDrivers = this.drivers();
        if (!selectedDrivers) return;

        const activeDrivers = this.driversControl
          .getRawValue()
          .map((group) => group.driver);

        const removedDrivers = differenceBy(
          activeDrivers,
          selectedDrivers,
          'uuid',
        );
        for (const driver of removedDrivers) {
          const index = this.driversControl
            .getRawValue()
            .findIndex((group) => group.uuid === driver.uuid);
          this.driversControl.removeAt(index);
        }

        const addedDrivers = differenceBy(
          selectedDrivers,
          activeDrivers,
          'uuid',
        );
        for (const driver of addedDrivers) {
          this.driversControl.push(
            BaseAssignedDriversFormComponent.buildAssignedDriverForm(
              this.fb,
              driver,
            ),
          );
        }

        this.driversControl.controls = [...this.driversControl.controls];
      },
      { injector: this.injector },
    );
  }

  protected assign(): void {
    if (!this.assignAllDrivers()) return;

    setTimeout(() => {
      this.assignAllDrivers.set(false);
      this.modalService.open<OnyxConfirmModalData, boolean>(
        OnyxConfirmModalComponent,
        {
          heading: 'bases.confirmModal.drivers.heading',
          message: 'bases.confirmModal.drivers.description',
          confirmButtonText: 'labels.assign',
          action: () =>
            UtilityHelper.maybeAsyncToObservable(
              this.DRIVERS_SOURCE.list('', {
                page: 1,
                limit: Number.MAX_SAFE_INTEGER,
              }),
            ).pipe(
              tap(({ options }) => {
                const drivers =
                  DropdownHelper.mapToOptions(options)?.map(
                    (option) => option.value.uuid,
                  ) ?? [];
                this.form().controls.selectedDrivers.setValue(drivers);
              }),
            ),
        },
      );
    });
  }

  protected openDriverModal(data: BaseAssignedDriversFormGroup): void {
    const driver = data.controls.driver.getRawValue();
    this.driverHelper.openModal(driver);
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      selectedDrivers: fb.control<string[]>([]),
      drivers: fb.array<BaseAssignedDriversFormGroup>([]),
    });
  }

  public static buildAssignedDriverForm(
    fb: NonNullableFormBuilder,
    driver: SimplifiedDriver,
  ) {
    return fb.group({
      driver: fb.control({ value: driver, disabled: true }),
      uuid: fb.control({ value: driver.uuid, disabled: true }),
    });
  }
}
